import React, {useMemo} from 'react';
import {reactQueryGetCategoryObjects} from '@netx/core-category/api/react-query';
import {CategoryAutoList, type CategoryAutoListProps} from './CategoryAutoList';

export interface CategoryAutoListUnknownProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends CategoryAutoListProps<Multiple, DisableClearable> {}

export const CategoryAutoListUnknownPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{value, ...props}: CategoryAutoListUnknownProps<Multiple, DisableClearable>,
	ref: any,
) => {
	const idsNeeded = useMemo(() => {
		return value == null
			? []
			: Array.isArray(value)
			? value.filter((v) => !v.label).map((v) => v.value)
			: !value.label
			? [value.value]
			: [];
	}, [value]);

	const categoryQuery = reactQueryGetCategoryObjects(
		{ids: idsNeeded},
		{
			enabled: !!idsNeeded.length,
			refetchOnWindowFocus: false,
		},
	);

	const valuesLoading = categoryQuery.isFetching;

	const path = useMemo(() => {
		if (props.multiple || !value || !categoryQuery.data) return '';
		return categoryQuery.data[0]?.path ?? '';
	}, [props.multiple, value, categoryQuery.data]);

	const _value = useMemo(() => {
		const isArray = Array.isArray(value);

		if (!value) return isArray ? [] : null;
		if (!categoryQuery.data) return value;

		return isArray
			? value.map((v) => ({
					...v,
					label:
						categoryQuery.data.find((d) => d.categoryid === v.value)?.name ??
						v.label,
			  }))
			: {
					...value,
					label:
						categoryQuery.data.find((d) => d.categoryid === value.value)
							?.name ?? value.label,
			  };
	}, [props.multiple, value, categoryQuery.data]);

	return (
		<CategoryAutoList<Multiple, DisableClearable>
			ref={ref}
			loadingValue={valuesLoading}
			path={path}
			{...props}
			value={_value as any}
		/>
	);
};

export const CategoryAutoListUnknown = React.forwardRef(
	CategoryAutoListUnknownPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: CategoryAutoListUnknownProps<Multiple, DisableClearable>,
) => JSX.Element;

export default CategoryAutoListUnknown;
